// /src/pages/UserManagementPage.jsx
// VERSÃO 20.0 SUPREMA - CONTINGENT MANAGEMENT & 7 ESTAÇÕES SYNC

import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import MainLayout from '../components/layout/MainLayout';
import { UserExecutionEngine } from '../api/UserExecutionEngine';
import { 
    FiUsers, FiTrash2, FiShield, FiLoader, FiUserPlus, FiX, 
    FiLock, FiUser, FiEdit3, FiBriefcase, FiKey, FiTerminal,
    FiRefreshCw, FiGrid, FiList, FiLayout, FiChevronRight, FiCpu, FiCheckCircle, FiChevronDown
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function UserManagementPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [userRole, setUserRole] = useState(null); 
    const [viewMode, setViewMode] = useState('excel'); 
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const [formData, setFormData] = useState({ 
        username: '', password: '', name: '', role: 'CORRETOR' 
    });

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await UserExecutionEngine.listarContingente();
            setUsers(Array.isArray(data) ? data : []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { 
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserRole(decoded.role?.toUpperCase());
            } catch (e) { console.error("Falha na decodificação de patente."); }
        }
        fetchUsers(); 
    }, []);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        
        const sucesso = editingUser 
            ? await UserExecutionEngine.atualizarAgente(editingUser._id, formData)
            : await UserExecutionEngine.alistarRecruta(formData);

        setTimeout(() => {
            if (sucesso) {
                setIsModalOpen(false);
                setEditingUser(null);
                setFormData({ username: '', password: '', name: '', role: 'CORRETOR' });
                fetchUsers();
            }
            setIsProcessing(false);
        }, 1500);
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setFormData({ name: user.name, username: user.username, role: user.role, password: '' });
        setIsModalOpen(true);
    };

    const getRoleLabel = (role) => {
        const labels = {
            'MASTER': 'Direção Geral',
            'GESTOR': 'Gestor Operacional',
            'CORRETOR': 'Especialista de Vendas',
            'AUXILIAR': 'Apoio Administrativo',
            'ESTAGIARIO': 'Assistente em Treino'
        };
        return labels[role] || role;
    };

    return (
        <MainLayout>
            {/* 📟 HUD DE PROCESSAMENTO (SYNC) */}
            <AnimatePresence>
                {isProcessing && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1000] bg-black/90 backdrop-blur-3xl flex flex-col items-center justify-center">
                        <div className="relative">
                            <FiCpu className="animate-spin mb-8 text-accent opacity-20" size={100} />
                            <div className="absolute inset-0 blur-3xl opacity-20 animate-pulse bg-accent" />
                        </div>
                        <h2 className="text-4xl font-black uppercase tracking-[0.5em] text-white">Sincronizando Patentes</h2>
                        <p className="text-[10px] font-bold uppercase tracking-[1em] text-accent mt-4 opacity-50">Protocolo de Segurança Ativo</p>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="w-full min-h-screen flex flex-col overflow-hidden transition-all duration-500"
                 style={{ backgroundColor: 'var(--bg-main)' }}>
                
                {/* 🛰️ HEADER DA CENTRAL (STATUS HUD) */}
                <header className="h-24 border-b px-8 flex items-center justify-between shadow-2xl transition-all"
                        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                    
                    <div className="flex items-center gap-10">
                        <div className="flex flex-col">
                            <h1 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3 text-auto leading-none mb-1.5">
                                <FiShield className="text-accent" size={20} /> Gestão de Contingente
                            </h1>
                            <p className="text-[9px] font-black uppercase tracking-[0.4em] opacity-30 text-auto">Controle de Hierarquia Operacional</p>
                        </div>

                        <div className="flex items-center p-1 rounded-xl border border-auto bg-main-auto shadow-inner">
                            <ViewBtn active={viewMode === 'excel'} onClick={() => setViewMode('excel')} icon={FiList} label="Grade" />
                            <ViewBtn active={viewMode === 'preview'} onClick={() => setViewMode('preview')} icon={FiLayout} label="Fichas" />
                            <ViewBtn active={viewMode === 'industrial'} onClick={() => setViewMode('industrial')} icon={FiGrid} label="Cards" />
                        </div>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="px-5 py-2 rounded-2xl border border-auto bg-main-auto flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[9px] font-black uppercase tracking-widest text-auto opacity-60">Status: Sincronizado</span>
                        </div>
                        <button 
                            onClick={() => { setEditingUser(null); setFormData({username:'', name:'', role:'CORRETOR', password:''}); setIsModalOpen(true); }}
                            className="px-8 py-4 bg-accent text-black rounded-[1.5rem] font-black uppercase text-[10px] tracking-[0.3em] shadow-2xl transition-all hover:scale-105 active:scale-95 flex items-center gap-3"
                        >
                            <FiUserPlus size={16} strokeWidth={3} /> Recrutar Agente
                        </button>
                    </div>
                </header>

                <div className="flex-1 flex overflow-hidden">
                    
                    {/* VISÃO 1: EXCEL (RADAR DE AGENTES) */}
                    {viewMode === 'excel' && (
                        <div className="flex-1 overflow-y-auto p-12 animate-in fade-in duration-500">
                            <div className="rounded-[2.5rem] border border-auto bg-card-auto overflow-hidden shadow-2xl">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-auto bg-main-auto">
                                            <th className="py-6 pl-10 text-[9px] font-black uppercase tracking-widest text-auto opacity-30">Status</th>
                                            <th className="text-[9px] font-black uppercase tracking-widest text-auto opacity-30">Agente Operacional</th>
                                            <th className="text-[9px] font-black uppercase tracking-widest text-auto opacity-30">Protocolo de Login</th>
                                            <th className="text-[9px] font-black uppercase tracking-widest text-auto opacity-30">Patente Atribuída</th>
                                            <th className="text-right pr-10 text-[9px] font-black uppercase tracking-widest text-auto opacity-30">Comando</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-auto">
                                        {users.map(user => (
                                            <tr key={user._id} className="group hover:bg-accent/[0.03] transition-colors">
                                                <td className="py-6 pl-10">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]" />
                                                </td>
                                                <td className="text-[12px] font-black uppercase tracking-tighter text-auto">{user.name}</td>
                                                <td className="text-[11px] font-bold text-sec-auto opacity-40">{user.username}</td>
                                                <td className="text-[11px] font-black text-accent tracking-widest uppercase">{getRoleLabel(user.role)}</td>
                                                <td className="text-right pr-10">
                                                    <div className="flex justify-end gap-3 opacity-20 group-hover:opacity-100 transition-all">
                                                        <button onClick={() => handleEditClick(user)} className="p-3 bg-card-auto border border-auto rounded-xl text-auto hover:border-accent hover:text-accent transition-all shadow-xl">
                                                            <FiEdit3 size={16}/>
                                                        </button>
                                                        {user.role !== 'MASTER' && (
                                                            <button onClick={() => {if(window.confirm("CONFIRMAR EXPULSÃO DE AGENTE?")) UserExecutionEngine.expulsarAgente(user._id).then(fetchUsers)}} 
                                                                    className="p-3 bg-card-auto border border-auto rounded-xl text-auto hover:border-red-500 hover:text-red-500 transition-all shadow-xl">
                                                                <FiTrash2 size={16}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* VISÃO 2: PREVIEW (DOSSIÊ DE AGENTE) */}
                    {viewMode === 'preview' && (
                        <>
                            <aside className="w-96 border-r border-auto bg-card-auto overflow-y-auto custom-scrollbar transition-all duration-500 p-4 space-y-2">
                                {users.map(user => (
                                    <div key={user._id} onClick={() => setSelectedUser(user)}
                                         className={`p-6 rounded-[1.5rem] border transition-all cursor-pointer relative group ${selectedUser?._id === user._id ? 'bg-main-auto border-accent/40 shadow-2xl' : 'border-transparent opacity-40 hover:opacity-100'}`}>
                                        {selectedUser?._id === user._id && (
                                            <div className="absolute left-0 top-6 bottom-6 w-1 bg-accent rounded-full" />
                                        )}
                                        <h4 className="text-[11px] font-black uppercase text-auto tracking-tighter truncate">{user.name}</h4>
                                        <p className="text-[8px] text-accent font-bold uppercase mt-1.5 tracking-[0.2em]">{getRoleLabel(user.role)}</p>
                                    </div>
                                ))}
                            </aside>
                            
                            <main className="flex-1 p-16 md:p-24 overflow-y-auto relative">
                                <div className="absolute -right-20 -top-20 w-80 h-80 opacity-5 blur-[120px] pointer-events-none bg-accent"></div>
                                
                                {selectedUser ? (
                                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                                                className="max-w-4xl mx-auto space-y-16">
                                        <header className="flex flex-col md:flex-row justify-between items-end border-b border-auto pb-12 gap-8">
                                            <div>
                                                <div className="flex items-center gap-4 mb-4 opacity-30">
                                                    <FiTerminal size={14} className="text-accent" />
                                                    <span className="text-[9px] font-black uppercase tracking-[0.6em] text-auto">Registro Centralizado</span>
                                                </div>
                                                <h2 className="text-5xl md:text-7xl font-black text-auto uppercase tracking-tighter leading-none">{selectedUser.name}</h2>
                                                <p className="text-[10px] text-accent font-black uppercase tracking-[0.5em] mt-6 flex items-center gap-3">
                                                    <div className="w-1 h-1 rounded-full bg-accent animate-pulse" /> ID DE OPERAÇÃO: {selectedUser._id}
                                                </p>
                                            </div>
                                            <button onClick={() => handleEditClick(selectedUser)} 
                                                    className="px-10 py-5 bg-card-auto border border-auto text-auto font-black uppercase text-[11px] tracking-[0.4em] rounded-[1.5rem] hover:border-accent hover:text-accent transition-all shadow-2xl active:scale-95">
                                                Editar Perfil
                                            </button>
                                        </header>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                            <div className="bg-card-auto border border-auto p-12 rounded-[3.5rem] shadow-2xl relative overflow-hidden group">
                                                <FiKey size={80} className="absolute -right-4 -bottom-4 text-accent opacity-5 group-hover:opacity-10 transition-opacity" />
                                                <h4 className="text-[10px] font-black text-auto opacity-30 uppercase tracking-[0.4em] mb-10 border-b border-auto pb-4">Credenciais de Missão</h4>
                                                <div className="space-y-6">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[9px] font-bold text-sec-auto opacity-20 uppercase tracking-widest">Protocolo de Login</span>
                                                        <p className="text-lg font-black text-auto uppercase">{selectedUser.username}</p>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[9px] font-bold text-sec-auto opacity-20 uppercase tracking-widest">Nível de Patente</span>
                                                        <p className="text-lg font-black text-accent uppercase">{getRoleLabel(selectedUser.role)}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-main-auto border border-auto p-12 rounded-[3.5rem] shadow-inner flex flex-col items-center justify-center text-center space-y-6">
                                                <div className="w-24 h-24 rounded-full border border-accent/20 bg-accent/5 flex items-center justify-center text-accent shadow-2xl">
                                                    <FiShield size={48} />
                                                </div>
                                                <div>
                                                    <h5 className="text-[10px] font-black uppercase text-auto mb-1">Status de Segurança</h5>
                                                    <p className="text-[11px] font-bold text-green-500 uppercase tracking-widest">Ativo e Verificado</p>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center opacity-10 space-y-8">
                                        <FiUsers size={120} className="text-auto" />
                                        <p className="text-[12px] font-black uppercase tracking-[0.8em] text-auto">Selecione um Agente para Auditoria</p>
                                    </div>
                                )}
                            </main>
                        </>
                    )}

                    {/* VISÃO 3: CARDS (HANGAR OPERACIONAL) */}
                    {viewMode === 'industrial' && (
                        <div className="flex-1 overflow-y-auto p-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10 animate-in zoom-in-95 duration-700">
                            {users.map(user => (
                                <div key={user._id} 
                                     className="group relative p-10 rounded-[3rem] border shadow-2xl transition-all duration-700 hover:-translate-y-2 border-auto bg-card-auto overflow-hidden">
                                    
                                    <div className="flex justify-between items-start mb-10">
                                        <div className="px-4 py-1.5 rounded-xl bg-accent/5 border border-accent/20 text-[8px] font-black text-accent uppercase tracking-widest">
                                            {getRoleLabel(user.role)}
                                        </div>
                                        <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_#22c55e]" />
                                    </div>
                                    
                                    <div className="space-y-2 mb-12">
                                        <h3 className="text-xl font-black text-auto uppercase tracking-tighter group-hover:text-accent transition-colors truncate">{user.name}</h3>
                                        <p className="text-[10px] font-bold text-sec-auto opacity-30 uppercase tracking-widest">{user.username}</p>
                                    </div>
                                    
                                    <button onClick={() => handleEditClick(user)} 
                                            className="w-full py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] transition-all flex items-center justify-center gap-3 border border-auto hover:bg-accent hover:text-black hover:border-accent bg-main-auto text-auto shadow-inner">
                                        Ajustar Perfil
                                    </button>
                                    
                                    <FiTerminal className="absolute -right-8 -bottom-8 text-accent opacity-0 group-hover:opacity-5 transition-opacity duration-1000" size={150} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 🛡️ MODAL DE RECRUTAMENTO / AJUSTE (GABINETE SYNC) */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-3xl p-6">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                                    className="w-full max-w-xl border shadow-[0_50px_100px_rgba(0,0,0,0.5)] p-12 rounded-[4rem] relative overflow-hidden"
                                    style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                            
                            <div className="absolute -top-24 -left-24 w-64 h-64 opacity-5 blur-[100px] bg-accent" />
                            
                            <header className="flex justify-between items-center mb-12 relative z-10">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 rounded-2xl border flex items-center justify-center text-accent bg-main-auto border-auto shadow-inner">
                                        <FiUserPlus size={28} />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-black uppercase tracking-tighter text-auto">
                                            {editingUser ? 'Ajuste de Patente' : 'Novo Recrutamento'}
                                        </h2>
                                        <p className="text-[10px] font-black text-accent uppercase tracking-[0.4em] mt-1.5 opacity-60">Terminal Diplomata v20.0</p>
                                    </div>
                                </div>
                                <button onClick={() => setIsModalOpen(false)} className="p-4 rounded-full text-auto opacity-30 hover:opacity-100 transition-all hover:rotate-90">
                                    <FiX size={28}/>
                                </button>
                            </header>

                            <form onSubmit={handleFormSubmit} className="space-y-8 relative z-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <EliteInput label="Nome Completo" icon={FiUser} value={formData.name} onChange={v => setFormData({...formData, name: v})} />
                                    <EliteInput label="Protocolo Login" icon={FiTerminal} value={formData.username} onChange={v => setFormData({...formData, username: v})} />
                                </div>
                                <EliteInput label={editingUser ? "Sobrescrever Senha (Opcional)" : "Senha de Acesso"} type="password" icon={FiKey} value={formData.password} onChange={v => setFormData({...formData, password: v})} />
                                
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-auto opacity-30 uppercase ml-4 tracking-[0.3em] flex items-center gap-2">
                                        <FiBriefcase size={12} className="text-accent" /> Função Operacional
                                    </label>
                                    <div className="relative group/select">
                                        <select 
                                            value={formData.role}
                                            onChange={e => setFormData({...formData, role: e.target.value})}
                                            className="w-full h-18 py-5 px-8 rounded-3xl text-auto text-[12px] font-black uppercase outline-none border border-auto bg-main-auto transition-all appearance-none cursor-pointer focus:border-accent shadow-inner"
                                        >
                                            <option value="CORRETOR" className="text-black bg-white">Especialista de Vendas</option>
                                            <option value="GESTOR" className="text-black bg-white">Gestor Operacional</option>
                                            <option value="AUXILIAR" className="text-black bg-white">Apoio Administrativo</option>
                                            <option value="ESTAGIARIO" className="text-black bg-white">Assistente (Trainee)</option>
                                            {userRole === 'MASTER' && (
                                                <option value="MASTER" className="text-accent bg-white font-black">Comandante Geral (Master)</option>
                                            )}
                                        </select>
                                        <FiChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-accent opacity-40 pointer-events-none group-hover/select:opacity-100 transition-all" size={20} />
                                    </div>
                                </div>

                                <button type="submit" className="w-full py-7 font-black uppercase text-base tracking-[0.5em] rounded-[2rem] shadow-2xl mt-8 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-5"
                                        style={{ backgroundColor: 'var(--accent-color)', color: '#000' }}>
                                    <FiCheckCircle size={24} /> {editingUser ? 'AUTORIZAR ALTERAÇÃO' : 'EFETIVAR ALISTAMENTO'}
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </MainLayout>
    );
}

// 💎 COMPONENTES AUXILIARES ELITE (SYNC)

const ViewBtn = ({ active, onClick, icon: Icon, label }) => (
    <button onClick={onClick} className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${active ? 'bg-accent text-black shadow-xl scale-105' : 'text-auto opacity-30 hover:opacity-100 hover:bg-card-auto'}`}>
        <Icon size={16} />
        {active && <span className="text-[10px] font-black uppercase tracking-tighter">{label}</span>}
    </button>
);

const EliteInput = ({ label, value, onChange, type = "text", icon: Icon }) => (
    <div className="space-y-3 group">
        <label className="text-[10px] font-black text-auto opacity-30 uppercase ml-4 tracking-[0.3em] group-focus-within:text-accent group-focus-within:opacity-100 transition-all flex items-center gap-2">
            <Icon size={12} className="text-accent opacity-50" /> {label}
        </label>
        <input 
            type={type} value={value} onChange={e => onChange(e.target.value)}
            className="w-full h-18 p-6 rounded-3xl text-auto text-[12px] font-black uppercase outline-none border border-auto bg-main-auto transition-all shadow-inner focus:border-accent placeholder:opacity-10"
            autoComplete="off"
        />
    </div>
);